import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeftIcon, ArrowRightIcon, XIcon } from "lucide-react";
import type { GalleryItem } from "../../data/gallery";
import { EASE } from "../../utils/motion";
type LightboxProps = {
  items: GalleryItem[];
  index: number;
  onClose: () => void;
  onChange: (index: number) => void;
};
export function Lightbox({
  items,
  index,
  onClose,
  onChange
}: LightboxProps) {
  const reduce = useReducedMotion();
  const [direction, setDirection] = useState(1);
  const item = items[index];
  const go = (delta: number) => {
    setDirection(delta);
    onChange((index + delta + items.length) % items.length);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  });
  if (!item) return null;
  return <motion.div role="dialog" aria-modal="true" aria-label={item.caption} initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: reduce ? 0.01 : 0.25,
    ease: EASE
  }} className="fixed inset-0 z-[90] flex flex-col bg-[#040f1c]/97 text-white" data-id="element-254" data-mp="1mep8boa7an3h">
      <div className="flex items-center justify-between px-5 py-5 md:px-9" data-id="element-255" data-mp="ylyvzdydy1so">
        <p className="text-sm tabular-nums text-white/60" data-id="element-256" data-mp="13meai8uelfc8">{String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}</p>
        <button type="button" onClick={onClose} autoFocus className="grid h-11 w-11 place-items-center border border-white/30 transition-colors duration-200 hover:border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" aria-label="Close gallery" data-id="element-257" data-mp="ayidt44u3nfg"><XIcon size={18} data-id="element-258" data-mp="7vb6gw1nluwu" /></button>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-5 md:px-24" data-id="element-259" data-mp="2y2mqr0uxith">
        <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-260" data-mp="hmfk9sflf9q3">
          <motion.img key={item.id} src={item.src} alt={item.alt} draggable={false} initial={{
          opacity: 0,
          x: reduce ? 0 : direction * 80,
          scale: reduce ? 1 : 0.97
        }} animate={{
          opacity: 1,
          x: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          x: reduce ? 0 : direction * -80
        }} transition={{
          duration: reduce ? 0.01 : 0.45,
          ease: EASE
        }} drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.2} onDragEnd={(_, info) => {
          if (info.offset.x < -70) go(1);
          if (info.offset.x > 70) go(-1);
        }} className="max-h-[68vh] w-auto max-w-full cursor-grab select-none object-contain active:cursor-grabbing" data-id="element-261" data-mp="hojlu3q0rwn7" />
        </AnimatePresence>
        <button type="button" onClick={() => go(-1)} className="absolute left-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 place-items-center border border-white/30 transition-colors duration-200 hover:bg-white hover:text-[#061f38] md:grid" aria-label="Previous image" data-id="element-262" data-mp="1hum3r1i5pywt"><ArrowLeftIcon size={18} data-id="element-263" data-mp="17wzizye6fd4x" /></button>
        <button type="button" onClick={() => go(1)} className="absolute right-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 place-items-center bg-[#c5233d] transition-colors duration-200 hover:bg-[#a71931] md:grid" aria-label="Next image" data-id="element-264" data-mp="1vls7j9sgyipi"><ArrowRightIcon size={18} data-id="element-265" data-mp="1mt720vdum8im" /></button>
      </div>

      <div className="px-5 pb-6 pt-5 md:px-9" data-id="element-266" data-mp="1qvk3v8ph5ma4">
        <div className="flex items-baseline justify-between gap-4" data-id="element-267" data-mp="1qu90vb3usik2">
          <AnimatePresence mode="wait" data-id="element-268" data-mp="2ekmpern8bz9w">
            <motion.p key={item.id} initial={{
            opacity: 0,
            y: reduce ? 0 : 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: reduce ? 0.01 : 0.3,
            ease: EASE
          }} className="text-xl font-medium tracking-[-0.03em]" data-id="element-269" data-mp="2ejv2043sd7up">
              {item.caption}
            </motion.p>
          </AnimatePresence>
          <span className="text-xs font-semibold tracking-[0.16em] text-[#f0b1b9]" data-id="element-270" data-mp="qqt4upw2ieij">{item.album.toUpperCase()}</span>
        </div>
        <div className="no-scrollbar mt-5 flex gap-2 overflow-x-auto" data-id="element-271" data-mp="hoa3ka7xk9ye">
          {items.map((it, i) => <button key={it.id} type="button" onClick={() => {
          setDirection(i > index ? 1 : -1);
          onChange(i);
        }} aria-label={`View ${it.caption}`} className={`h-14 w-20 shrink-0 overflow-hidden transition-opacity duration-200 ${i === index ? "opacity-100 ring-2 ring-[#c5233d]" : "opacity-40 hover:opacity-80"}`} data-id="element-272" data-mp="1eeaq02ktuecj" data-mpi={i}>
              <img src={it.src} alt="" aria-hidden="true" loading="lazy" className="h-full w-full object-cover" data-id="element-273" data-mp="10ll0vpa04tb7" />
            </button>)}
        </div>
      </div>
    </motion.div>;
}